import type { ReactNode } from 'react';
import {
  createFileRoute,
  Link,
  Outlet,
  useRouterState,
} from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { triggerHaptic } from '@repo/shared/client';
import { isAdminRole } from '@repo/shared/common';
import { currentOrganizationQueryOptions } from '~/lib/queries/organization';
import { Building2, User, ListChecks, ShieldCheck } from '~/components/icons';
import { Stack, Title } from '~/components/ui';
import styles from './route.module.css';

// Layout for the Settings section (/settings/*): a "Settings" heading + a secondary
// left nav (Organization / Profile / Team, plus Security for admins) beside the
// routed panel. The nav stacks above the panel on the mobile tier and sits in its
// own column from desktop.
export const Route = createFileRoute('/_protected/settings')({
  component: SettingsLayout,
});

/**
 * Settings sub-nav entries, in order. `exact` matches the index (Organization).
 * `requiresAdmin` entries are filtered out for non-admins (mirrors the shell's
 * `requiresEdit` filter; the route's own guard enforces the same boundary).
 */
const SETTINGS_NAV = [
  { to: '/settings', labelKey: 'settings.nav.organization', Icon: Building2, exact: true },
  { to: '/settings/profile', labelKey: 'settings.nav.profile', Icon: User, exact: false },
  { to: '/settings/team', labelKey: 'settings.nav.team', Icon: ListChecks, exact: false },
  {
    to: '/settings/security',
    labelKey: 'settings.nav.security',
    Icon: ShieldCheck,
    exact: false,
    requiresAdmin: true,
  },
] as const;

type SettingsRoute = (typeof SETTINGS_NAV)[number]['to'];

function SettingsLayout(): ReactNode {
  const { t } = useTranslation();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  // Live role read against the same current-org cache entry the `_protected`
  // gate warmed, so a role change refetch updates the nav without a reload.
  // Null mid-resolve is treated as non-admin — fail-closed.
  const { data: orgData } = useQuery(currentOrganizationQueryOptions());
  const isAdmin = orgData?.role ? isAdminRole(orgData.role) : false;
  const navItems = SETTINGS_NAV.filter(
    (item) => !('requiresAdmin' in item && item.requiresAdmin) || isAdmin,
  );

  return (
    <Stack gap="xl" data-testid="settings-page">
      <Title order={1}>{t('settings.title')}</Title>

      <div className={styles.layout ?? ''}>
        <nav aria-label={t('settings.title')} data-testid="settings-nav">
          <ul className={styles.nav ?? ''}>
            {navItems.map(({ to, labelKey, Icon, exact }) => {
              const active = exact ? pathname === to : pathname.startsWith(to);
              return (
                <li key={to}>
                  <SettingsNavLink to={to} active={active} exact={exact}>
                    <Icon size={18} weight={active ? 'fill' : 'light'} />
                    {t(labelKey)}
                  </SettingsNavLink>
                </li>
              );
            })}
          </ul>
        </nav>

        <div>
          {/* Switching sub-tabs remounts just the panel route (the secondary nav
              above lives outside this wrapper and stays put), so the incoming panel's
              own <Reveal> cascade settles it in — no separate route fade needed. */}
          <Outlet />
        </div>
      </div>
    </Stack>
  );
}

function SettingsNavLink({
  to,
  active,
  exact,
  children,
}: {
  readonly to: SettingsRoute;
  readonly active: boolean;
  readonly exact: boolean;
  readonly children: ReactNode;
}): ReactNode {
  const className = [styles.navItem, active ? styles.navItemActive : undefined]
    .filter(Boolean)
    .join(' ');
  return (
    <Link
      to={to}
      className={className}
      // Match the index route ("/settings") exactly so it isn't flagged active on
      // "/settings/profile". TanStack owns aria-current (a single source of truth),
      // aligned to the same exact/prefix rule that drives the highlight class above
      // — otherwise the prefix-matched index link left two aria-current="page" items.
      activeOptions={{ exact }}
      data-testid={`settings-nav-${to.slice('/settings'.length) || '/organization'}`}
      onClick={() => triggerHaptic()}
    >
      {children}
    </Link>
  );
}
